/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import Text from "@CubeComponents/html/Text/Text";
import IconComponent from "@CubeComponents/icons";
import { getDefaultModuleMapper } from "@Features/portal-settings/components/PortalSetings/Configuration/Modules/constants";
import {
  defaultModuleNamesMapper,
  defaultModulesMapper,
  folderRoles,
  formCategories,
  noAccessObj,
  noAccessPermissions,
  RESOURCE_TYPE_SPACE,
} from "@Helpers/constants";
import { Accordion, AccordionDetails, AccordionSummary, FormControlLabel, RadioGroup } from "@mui/material";
import { t } from "@i18n";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { RootState } from "@Reducers/index";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import ToggleSwitchComponent from "@CubeComponents/form/ToggleSwitchComponent";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import { defaultAccessFlowSources, setModulePermissions } from "@Components/ModuleUsersAccess/constants";
import { TOAST_TYPE_ERROR } from "@Services/constants";
import { showToastNotification } from "@Reducers/toastSlice";
import RadioButton from "@CubeComponents/Mui_Form/RadioButton";
import axiosWrapper from "@AxiosWrapper";
import type { AccessTemplateControllerResponse } from "@ServerTypes/permissions/permissions.schema";
import { getBackendUrl } from "@Utils";
import type { CustomModuleItem } from "@ServerTypes/portal/portal.schema";

/** One module's entry inside a space user's / access template's `modulePermissions` bag. */
type ModulePermissionEntry = {
  role?: number;
  permissions?: number[];
  canCreate?: boolean;
  /** Set when a multi-row selection disagrees, so the cell renders "varies" instead of a value. */
  isRoleVaries?: boolean;
  isCanCreateVaries?: boolean;
  isCreatePermissionRecursive?: boolean;
  isAccessPermissionRecursive?: boolean;
  /** Product-seat counts, keyed by a module's `moduleType` ("CDE" / "PM"); 0 means no seat. */
  seat?: Record<string, number | undefined> | null;
};

type ModulePermissionMap = Record<string, ModulePermissionEntry>;

/** Which of the three outcomes a pending change was set to, per module. */
type PermissionChangeFlags = { retain?: boolean; overRide?: boolean; change?: boolean };

type ModulePermissionChangeStatus = {
  createPermission: Record<string, PermissionChangeFlags>;
  accessPermission: Record<string, PermissionChangeFlags>;
};

/** A row of the permission table: a default `ModuleCard`, or one of the synthetic custom-module rows. */
type PermissionRow = {
  id: string;
  name?: string;
  icon?: string;
  module: string;
  moduleType?: string;
  customModule?: boolean;
  allow?: boolean;
  resourceType?: string;
};

/** One accordion head: a `ModuleGroup`, or the synthetic "Add Ons" group built from custom modules. */
type PermissionGroup = { id: string; name: string; modules: PermissionRow[] };

/** A role option offered by `setModulePermissions` — a `folderRoles()` entry re-keyed with `name`. */
type RolePermissionOption = { id?: number; name?: string; frontendName?: string; description?: string };

/** An access-template row from `permissions/accessTemplateController`'s list branch. */
type AccessTemplateOption = { _id?: string; name?: string; modulePermissions?: ModulePermissionMap };

/**
 * What this component publishes into the ref its parent passes. Every member is optional because the
 * effects below build it up incrementally by spreading whatever is already there.
 */
type MapperRefValue = {
  changedPermissionMapper?: Record<string, unknown> | null;
  allPermissionsMapper?: Record<string, unknown> | null;
  newTemplateId?: string | null;
  resetStatuses?: () => void;
};

type FolderRoleName = keyof ReturnType<typeof folderRoles>;
/** A `folderRoles()` entry re-keyed by its numeric id, keeping the original map key as `key`. */
type FolderRoleEntry = ReturnType<typeof folderRoles>[FolderRoleName] & { key: string };

type SpaceUsersPermissionFlowProps = {
  showUpdateDisclaimer?: boolean;
  permissionMapper?: ModulePermissionMap | null;
  showAccessPermissionConfirmationPopup?: boolean;
  defaultEditState?: boolean;
  showCreatePermissionConfirmationPopup?: boolean;
  isAllDisabled?: boolean;
  showAccessTemplateChooser?: boolean;
  /** An access-template id, or the sentinel "Varies" when the selection spans several templates. */
  accessTemplateId?: string | null;
  getIsRecursive?: (value: boolean) => void;
  source?: string;
};

const SpaceUsersPermissionFlow = forwardRef(
  (
    {
      showUpdateDisclaimer = false,
      permissionMapper,
      showAccessPermissionConfirmationPopup = true,
      defaultEditState = true,
      showCreatePermissionConfirmationPopup = false,
      isAllDisabled = false,
      showAccessTemplateChooser = false,
      accessTemplateId,
      getIsRecursive,
      source,
    }: SpaceUsersPermissionFlowProps,
    ref: React.ForwardedRef<MapperRefValue>,
  ) => {
    const layout = useSelector((state: RootState) => state.layout);
    const customModules = useSelector((state: RootState) => state.customModules);
    const moduleMapper = getDefaultModuleMapper(layout);
    const folderRolesData = folderRoles();
    const [modulePermissionChangeStatus, setModulePermissionChangeStatus] = useState<ModulePermissionChangeStatus>({
      createPermission: {},
      accessPermission: {},
    });
    const [changedPermissionMapper, setChangedPermissionMapper] = useState<ModulePermissionMap>({});
    const [editState, setEditState] = useState<boolean>(defaultEditState);
    /** Per-module flags marking a "varies" cell the user chose to edit anyway. */
    const [cellEditMapper, setCellEditMapper] = useState<Record<string, { creation?: boolean; role?: boolean }>>({});
    const [initialPermissionMapper, setInitialPermissionMapper] = useState<ModulePermissionMap>(permissionMapper ?? {});
    const [recursiveChangePermission, setRecursiveChangePermission] = useState<boolean>(false);
    const tempRef = useRef<MapperRefValue>({
      changedPermissionMapper: null,
      allPermissionsMapper: null,
    });
    const overAllAccessChangeWarning = t(
      "If default access is updated from here, there are options to override the access of all entities in this space or to leave them as is.",
    );
    const folderAccessChangeWarning = t(
      "Updating the Default Access for Folders will also update the Default Access for Files, Contracts, Schedules and Estimates module",
    );

    // Callers always hand this component an object ref (`useRef`), never a callback ref, and the
    // effects below write into it — so the ForwardedRef union is narrowed once, here. An object ref
    // starts out holding `null`, which is what the `?? {}` spreads below are for.
    const mapperRef = typeof ref === "object" && ref !== null ? ref : tempRef;
    const [confirmationModelOpen, setConfirmationModelOpen] = useState<{
      status: boolean;
      /** Absent on the change-access-template branch, which does not target a single module. */
      module?: string;
      /** A boolean for creation-permission changes, the picked role option for access changes, and the template id for template changes. */
      value?: boolean | RolePermissionOption | string;
      permissionName: string;
    }>({
      status: false,
      module: "",
      value: {},
      permissionName: "",
    });
    const [accessTemplateDatasource, setAccessTemplateDatasource] = useState<AccessTemplateOption[]>([]);
    const [accessTemplateChooserState, setAccessTemplateChooserState] = useState<{
      currVal?: string | null;
      newVal?: string | null;
      state?: string;
      /** Truthy/falsy only — the effect below assigns the result of an `&&` chain, not a clean boolean. */
      show?: boolean | string | null;
    }>({
      currVal: accessTemplateId,
      newVal: null,
      state: "",
      show: showAccessTemplateChooser,
    });

    useEffect(() => {
      mapperRef.current = {
        ...(mapperRef.current ?? {}),
        changedPermissionMapper: changedPermissionMapper,
      };
    }, [changedPermissionMapper, mapperRef]);

    useEffect(() => {
      mapperRef.current = {
        ...(mapperRef.current ?? {}),
        newTemplateId: accessTemplateChooserState.newVal,
      };
    }, [accessTemplateChooserState.newVal, mapperRef]);

    useEffect(() => {
      if (showAccessTemplateChooser) {
        setAccessTemplateChooserState({
          currVal: accessTemplateId,
          show: accessTemplateId && accessTemplateId !== "Varies",
          state: accessTemplateId === "Varies" ? "Varies" : !accessTemplateId ? "Custom" : "",
        });
      }
    }, [showAccessTemplateChooser, accessTemplateId]);

    const fetchAccessTemplates = async () => {
      // SCHEMA GAP: `accessTemplateControllerResponse` documents that the list branch returns the
      // template array in `result` but does not declare the field, so it is named in the response
      // type this call reads the body at rather than asserted onto the body afterwards.
      const res = await axiosWrapper.post<AccessTemplateControllerResponse & { result?: AccessTemplateOption[] }>(
        `${getBackendUrl()}/permissions/accessTemplateController`,
        {},
      );
      if (res.data.status) {
        setAccessTemplateDatasource(res.data.result ?? []);
      }
    };

    useEffect(() => {
      if (showAccessTemplateChooser) {
        fetchAccessTemplates();
      }
    }, [showAccessTemplateChooser]);

    useEffect(() => {
      mapperRef.current = {
        ...(mapperRef.current ?? {}),
        allPermissionsMapper: initialPermissionMapper,
      };
    }, [initialPermissionMapper, mapperRef]);

    useEffect(() => {
      if (!permissionMapper) {
        const defaultModulePermissions: ModulePermissionMap = {
          ...noAccessPermissions,
        };
        // `resourceType` is optional in the schema but the server derives it from `name` on every
        // insert/update, so a persisted custom module always carries one.
        customModules.forEach((cm: CustomModuleItem) => {
          defaultModulePermissions[cm.resourceType!] = noAccessObj;
        });

        setInitialPermissionMapper(defaultModulePermissions);
      }
      mapperRef.current = {
        ...(mapperRef.current ?? {}),
        resetStatuses: () => {
          setModulePermissionChangeStatus({
            createPermission: {},
            accessPermission: {},
          });
        },
      };
    }, [customModules]);

    const permissionIdMapper = useMemo(() => {
      const obj: Record<number, FolderRoleEntry> = {};
      // `Object.entries` keeps each role next to the key it was stored under, so neither has to be
      // re-asserted back onto the map.
      Object.entries(folderRolesData).forEach(([key, role]) => {
        obj[role.id] = { ...role, key };
      });
      return obj;
    }, []);

    useEffect(() => {
      setEditState(!!defaultEditState);
      if (!defaultEditState) {
        setModulePermissionChangeStatus({
          createPermission: {},
          accessPermission: {},
        });
      }
    }, [defaultEditState]);

    useEffect(() => {
      if (permissionMapper) {
        setInitialPermissionMapper({ ...permissionMapper });
      }
    }, [permissionMapper]);

    const dispatch = useDispatch();

    const getUpdateModalDisclaimer = (content: React.ReactNode) => (
      <div
        className={`border p-2 border-banner_color_yellow bg-banner_color_yellow_translucent w-fit ps-3 gap-2 pe-3 rounded-lg flex items-center`}
      >
        <span
          className={`min-w-5 min-h-5 flex items-center justify-center rounded-full text-white text-xs cursor-default bg-yellow-400`}
        >
          !
        </span>
        <span>{content}</span>
      </div>
    );

    const itemTemplate = (data: RolePermissionOption) => {
      return (
        <div className="flex gap-3 p-1 items-center">
          <div>
            <IconComponent name={data.name} width="1.5rem" height="1.5rem" />
          </div>
          <div className="flex flex-col">
            <div className="font-semibold">{data.frontendName}</div>
            <div className="text-xs whitespace-normal">{data.description}</div>
          </div>
        </div>
      );
    };

    const getToggleValue = (data: PermissionRow) => {
      let module = data.customModule ? data.id : data.module;
      const perObj = initialPermissionMapper[module]?.canCreate;
      return !!perObj;
    };

    const isVaries = (data: PermissionRow, field: "isRoleVaries" | "isCanCreateVaries") => {
      const module = data.customModule ? data.id : data.module;
      const perObj = initialPermissionMapper[module]?.[field];
      return !!perObj;
    };

    const changeCreatePermission = (moduleName: string, checked: boolean) => {
      setModulePermissionChangeStatus((prev) => {
        let obj: Record<string, PermissionChangeFlags> = {
          [moduleName]: {
            retain: !recursiveChangePermission && showCreatePermissionConfirmationPopup,
            overRide: recursiveChangePermission && showCreatePermissionConfirmationPopup,
            change: !showCreatePermissionConfirmationPopup,
          },
        };
        if (moduleName === defaultModulesMapper.FOLDERS) {
          obj = {
            ...obj,
            [defaultModulesMapper.ESTIMATES]: {
              retain: !recursiveChangePermission && showCreatePermissionConfirmationPopup,
              overRide: recursiveChangePermission && showCreatePermissionConfirmationPopup,
              change: !showCreatePermissionConfirmationPopup,
            },
            [defaultModulesMapper.SCHEDULES]: {
              retain: !recursiveChangePermission && showCreatePermissionConfirmationPopup,
              overRide: recursiveChangePermission && showCreatePermissionConfirmationPopup,
              change: !showCreatePermissionConfirmationPopup,
            },
            [defaultModulesMapper.FILES]: {
              retain: !recursiveChangePermission && showCreatePermissionConfirmationPopup,
              overRide: recursiveChangePermission && showCreatePermissionConfirmationPopup,
              change: !showCreatePermissionConfirmationPopup,
            },
            [defaultModulesMapper.CONTRACTS]: {
              retain: !recursiveChangePermission && showCreatePermissionConfirmationPopup,
              overRide: recursiveChangePermission && showCreatePermissionConfirmationPopup,
              change: !showCreatePermissionConfirmationPopup,
            },
          };
        } else if (
          [
            defaultModulesMapper.ESTIMATES,
            defaultModulesMapper.SCHEDULES,
            defaultModulesMapper.FILES,
            defaultModulesMapper.CONTRACTS,
          ].includes(moduleName)
        ) {
          obj = {
            ...obj,
            [defaultModulesMapper.FOLDERS]: {
              retain: !recursiveChangePermission && showCreatePermissionConfirmationPopup,
              overRide: recursiveChangePermission && showCreatePermissionConfirmationPopup,
              change: !showCreatePermissionConfirmationPopup,
            },
          };
        }
        return {
          ...prev,
          createPermission: {
            ...prev.createPermission,
            ...obj,
          },
        };
      });

      setChangedPermissionMapper((prev) => {
        const currentObj = prev[moduleName] || initialPermissionMapper[moduleName];
        const contractObj =
          prev[defaultModulesMapper.CONTRACTS] || initialPermissionMapper[defaultModulesMapper.CONTRACTS];
        const filesObj = prev[defaultModulesMapper.FILES] || initialPermissionMapper[defaultModulesMapper.FILES];
        const schedulesObj =
          prev[defaultModulesMapper.SCHEDULES] || initialPermissionMapper[defaultModulesMapper.SCHEDULES];
        const estimatesObj =
          prev[defaultModulesMapper.ESTIMATES] || initialPermissionMapper[defaultModulesMapper.ESTIMATES];
        const foldersObj = prev[defaultModulesMapper.FOLDERS] || initialPermissionMapper[defaultModulesMapper.FOLDERS];
        let obj: ModulePermissionMap = {
          [moduleName]: {
            ...currentObj,
            isCreatePermissionRecursive: recursiveChangePermission,
            canCreate: checked,
          },
        };
        if (moduleName === defaultModulesMapper.FOLDERS) {
          obj = {
            ...obj,
            [defaultModulesMapper.CONTRACTS]: {
              ...contractObj,
              isCreatePermissionRecursive: recursiveChangePermission,
              canCreate: checked,
            },
            [defaultModulesMapper.FILES]: {
              ...filesObj,
              isCreatePermissionRecursive: recursiveChangePermission,
              canCreate: checked,
            },
            [defaultModulesMapper.SCHEDULES]: {
              ...schedulesObj,
              isCreatePermissionRecursive: recursiveChangePermission,
              canCreate: checked,
            },
            [defaultModulesMapper.ESTIMATES]: {
              ...estimatesObj,
              isCreatePermissionRecursive: recursiveChangePermission,
              canCreate: checked,
            },
          };
        } else if (
          [
            defaultModulesMapper.ESTIMATES,
            defaultModulesMapper.SCHEDULES,
            defaultModulesMapper.FILES,
            defaultModulesMapper.CONTRACTS,
          ].includes(moduleName) &&
          checked
        ) {
          obj = {
            ...obj,
            [defaultModulesMapper.FOLDERS]: {
              ...foldersObj,
              isCreatePermissionRecursive: recursiveChangePermission,
              canCreate: checked,
            },
          };
        }
        return {
          ...prev,
          ...obj,
        };
      });

      setInitialPermissionMapper((prev) => {
        let obj: ModulePermissionMap = {
          [moduleName]: {
            ...prev[moduleName],
            canCreate: checked,
          },
        };
        if (moduleName === defaultModulesMapper.FOLDERS) {
          obj = {
            ...obj,
            [defaultModulesMapper.CONTRACTS]: {
              ...prev[defaultModulesMapper.CONTRACTS],
              canCreate: checked,
            },
            [defaultModulesMapper.FILES]: {
              ...prev[defaultModulesMapper.FILES],
              canCreate: checked,
            },
            [defaultModulesMapper.SCHEDULES]: {
              ...prev[defaultModulesMapper.SCHEDULES],
              canCreate: checked,
            },
            [defaultModulesMapper.ESTIMATES]: {
              ...prev[defaultModulesMapper.ESTIMATES],
              canCreate: checked,
            },
          };
        } else if (
          [
            defaultModulesMapper.ESTIMATES,
            defaultModulesMapper.SCHEDULES,
            defaultModulesMapper.FILES,
            defaultModulesMapper.CONTRACTS,
          ].includes(moduleName) &&
          checked
        ) {
          obj = {
            ...obj,
            [defaultModulesMapper.FOLDERS]: {
              ...prev[defaultModulesMapper.FOLDERS],
              canCreate: checked,
            },
          };
        }
        return {
          ...prev,
          ...obj,
        };
      });
      setAccessTemplateChooserState((prev) => ({
        ...prev,
        show: false,
        state: "Custom",
      }));
      setRecursiveChangePermission(false);
      handleCloseRecursiveModal();
    };
    const handleToggleChange = (checked: boolean, data: PermissionRow) => {
      const module = data.customModule ? data.id : data.module;
      if (
        showCreatePermissionConfirmationPopup ||
        module === defaultModulesMapper.FOLDERS ||
        ([
          defaultModulesMapper.SCHEDULES,
          defaultModulesMapper.FILES,
          defaultModulesMapper.CONTRACTS,
          defaultModulesMapper.ESTIMATES,
        ].includes(module) &&
          checked)
      ) {
        setConfirmationModelOpen({
          status: true,
          module,
          value: checked,
          permissionName: "createPermission",
        });
      } else {
        changeCreatePermission(module, checked);
      }
    };

    // Returns one of the very objects `getPermissionOptions` hands the picker, so the controlled
    // `value` matches an option by identity. The previous shape (`{ id, frontendName }`) carried
    // two of the option's five members and had to be asserted into place.
    const getPermissionValue = (data: PermissionRow) => {
      // The role is stored against the row's own module key; the FORMS re-mapping applies only to
      // the option list, which `getPermissionOptions` already does for itself.
      const module = data.customModule ? data.id : data.module;
      const roleId = initialPermissionMapper[module]?.role;
      if (isVaries(data, "isRoleVaries")) {
        return null;
      }
      return getPermissionOptions(data).find((option) => option.id === roleId) ?? null;
    };

    const additionalvalidations = ({ option, data }: { option: RolePermissionOption; data: PermissionRow }) => {
      if (
        (data.customModule ||
          [
            formCategories.bills,
            formCategories.changeOrder,
            formCategories.inspection,
            formCategories.progressUpdates,
          ].includes(data.module)) &&
        (option.id == folderRolesData.RESOURCE_ADMIN.id || option.id == folderRolesData.RESOURCE_EDITOR.id)
      ) {
        dispatch(
          showToastNotification({
            content: t("This permission can only be updated through the corresponding form flow."),
            type: TOAST_TYPE_ERROR,
          }),
        );
        return false;
      } else if (
        data.module === defaultModulesMapper.REVIEWS &&
        (option.id == folderRolesData.RESOURCE_ADMIN.id || option.id == folderRolesData.RESOURCE_EDITOR.id)
      ) {
        dispatch(
          showToastNotification({
            content: t("This permission can only be updated through the corresponding review flow."),
            type: TOAST_TYPE_ERROR,
          }),
        );
        return false;
      }
      return true;
    };

    const setUserPermissionData = ({ option, data }: { option: RolePermissionOption; data: PermissionRow }) => {
      const res = additionalvalidations({ option, data });
      if (!res) {
        return;
      }
      const module = data.customModule ? data.id : data.module;
      // The right-hand side is only reached with `showAccessPermissionConfirmationPopup` false.
      if (showAccessPermissionConfirmationPopup || module === defaultModulesMapper.FOLDERS) {
        setConfirmationModelOpen({
          status: true,
          module,
          value: option,
          permissionName: "accessPermission",
        });
      } else {
        handleChangeAccessPermission(module, option.id!);
      }
    };

    const getPermissionOptions = (data: PermissionRow) => {
      let module = data.module;
      if (
        [
          formCategories.bills,
          formCategories.changeOrder,
          formCategories.inspection,
          formCategories.progressUpdates,
        ].includes(data.module) ||
        data.customModule
      ) {
        module = "FORMS";
      }
      return setModulePermissions(module);
    };

    const handleEditCellClick = (module: string, field: string) => {
      setCellEditMapper((prev) => ({
        ...prev,
        [module]: {
          ...prev[module],
          [field]: true,
        },
      }));
    };

    const getToggle = (data: PermissionRow) => {
      const module = data.customModule ? data.id : data.module;
      const seat = initialPermissionMapper[module]?.seat;

      if (seat && !seat[data.moduleType!]) {
        return <Text>{t("No Seat")}</Text>;
      }
      if (
        isVaries(data, "isCanCreateVaries") &&
        !cellEditMapper[module]?.creation &&
        ![
          defaultModulesMapper.FILES,
          defaultModulesMapper.FOLDERS,
          defaultModulesMapper.CONTRACTS,
          defaultModulesMapper.ESTIMATES,
          defaultModulesMapper.SCHEDULES,
        ].includes(module)
      ) {
        return (
          <div className="flex w-full justify-between items-center">
            <Text>{t("varies")}</Text>
            <IconComponent
              className="cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => handleEditCellClick(module, "creation")}
              name="editProfile"
              width="1.25rem"
              height="1.25rem"
            />
          </div>
        );
      }
      if (!editState) {
        return getToggleValue(data) ? t("Yes") : t("No");
      }
      const toggleValue = getToggleValue(data);
      return (
        <ToggleSwitchComponent
          disabled={isAllDisabled}
          checked={toggleValue}
          onChange={(_, checked) => handleToggleChange(checked, data)}
        />
      );
    };

    const getPermissionDropDown = (data: PermissionRow) => {
      const module = data.customModule ? data.id : data.module;
      const seat = initialPermissionMapper[module]?.seat;

      if (seat && !seat[data.moduleType!]) {
        return <Text>{t("No Seat")}</Text>;
      }
      if (isVaries(data, "isRoleVaries") && !cellEditMapper[module]?.role) {
        return (
          <div className="flex w-full justify-between items-center">
            <Text>{t("varies")}</Text>
            <IconComponent
              onClick={() => handleEditCellClick(module, "role")}
              className="cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
              name="editProfile"
              width="1.25rem"
              height="1.25rem"
            />
          </div>
        );
      }
      if (!editState) {
        return getPermissionValue(data)?.frontendName;
      }
      return (
        <SelectDropDown
          id={`${data.id}`}
          renderOption={itemTemplate}
          onChange={(option) => {
            // Single-select: the multi-value and cleared arms of SelectDropDown never arrive here.
            if (!option || Array.isArray(option)) return;
            setUserPermissionData({ option, data });
          }}
          value={getPermissionValue(data)}
          buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
          fields={{ text: "frontendName", value: "id" }}
          options={getPermissionOptions(data)}
          buttonWidth="100%"
          popupAlign="right"
          disabled={isAllDisabled}
          popperWidth={400}
        />
      );
    };

    const getIconForChange = (data: PermissionRow, permissionName: keyof ModulePermissionChangeStatus) => {
      let module = data.customModule ? data.id : data.module;
      if (modulePermissionChangeStatus[permissionName][module]?.change) {
        return <IconComponent name="details" width="1.5rem" height="1.5rem" toolTipContent={t("Set to change")} />;
      } else if (modulePermissionChangeStatus[permissionName][module]?.retain) {
        return (
          <IconComponent name="details" width="1.5rem" height="1.5rem" toolTipContent={t("Set to change and retain")} />
        );
      } else if (modulePermissionChangeStatus[permissionName][module]?.overRide) {
        return (
          <IconComponent
            name="details"
            width="1.5rem"
            height="1.5rem"
            toolTipContent={t("Set to change and override")}
          />
        );
      }
      return <></>;
    };

    const permissionContent = (module: PermissionGroup) => {
      const list = module.modules.filter((m) => m.resourceType === RESOURCE_TYPE_SPACE);
      return (
        <div className={`grid grid-cols-3 border-t border-stroke_primary_light`}>
          {list.map((data) => (
            <React.Fragment key={data.id}>
              <div className="border-b flex items-center border-e border-stroke_primary_light p-3">
                <div className="flex items-center gap-2 overflow-hidden">
                  <IconComponent name={data.icon} width="1.25rem" height="1.25rem" />
                  <div className="overflow-hidden whitespace-nowrap text-ellipsis">{data.name}</div>
                </div>
              </div>
              <div
                className={`group border-b border-e items-center justify-between flex gap-2 border-stroke_primary_light p-3 ${
                  isVaries(data, "isCanCreateVaries") ? "hover:bg-light_background" : ""
                }`}
              >
                {getToggle(data)}
                {getIconForChange(data, "createPermission")}
              </div>
              <div
                className={`group border-b border-e justify-between items-center flex gap-1 border-stroke_primary_light p-3 ${
                  isVaries(data, "isRoleVaries") ? "hover:bg-light_background" : ""
                }`}
              >
                {getPermissionDropDown(data)}
                {getIconForChange(data, "accessPermission")}
              </div>
            </React.Fragment>
          ))}
        </div>
      );
    };

    const addOnObj = useMemo(
      () => ({
        name: t("Add Ons"),
        id: "customModules",
        modules: customModules.map((module: CustomModuleItem) => ({
          id: module.resourceType!,
          name: module.name,
          icon: module.icon,
          customModule: true,
          module: "CUSTOM_MODULE",
          allow: true,
          resourceType: RESOURCE_TYPE_SPACE,
        })),
      }),
      [customModules],
    );

    const handleSelectTemplate = (args: AccessTemplateOption | AccessTemplateOption[] | null) => {
      // Single-select: the multi-value and cleared arms of SelectDropDown never arrive here.
      if (!args || Array.isArray(args)) return;
      setConfirmationModelOpen({
        status: true,
        value: args._id,
        permissionName: "changeAccessTemplate",
      });
    };

    const getAccessTemplateChooser = () => (
      <div className="w-full flex gap-4 items-center">
        <Text className="whitespace-normal">{t("Access Templates")} :</Text>
        {accessTemplateChooserState.show && (
          <SelectDropDown
            value={accessTemplateChooserState.newVal || accessTemplateChooserState.currVal}
            fields={{ text: "name", value: "_id" }}
            options={accessTemplateDatasource}
            buttonWidth={"31.25rem"}
            popperWidth={500}
            buttonType={BUTTON_TYPES.BORDER_BUTTON}
            onChange={handleSelectTemplate}
          />
        )}

        {accessTemplateChooserState.state && (
          <div className="group w-36 border border-stroke_primary_light pt-1 pb-1 ps-2 pe-2 rounded items-center flex justify-between">
            <Text>{accessTemplateChooserState.state}</Text>
            <IconComponent
              className="cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => setAccessTemplateChooserState((prev) => ({ ...prev, show: !prev.show }))}
              name={accessTemplateChooserState.show ? "cross" : "editProfile"}
              width="1rem"
              height="1rem"
            />
          </div>
        )}
      </div>
    );

    const updatePermissionModalContent = () => {
      return (
        <div className="flex h-full w-full flex-col gap-4">
          {showUpdateDisclaimer && getUpdateModalDisclaimer(overAllAccessChangeWarning)}
          {showAccessTemplateChooser && getAccessTemplateChooser()}
          <div className="flex flex-col">
            <div
              className={`grid grid-cols-3 border-t border-s border-e bg-light_background border-stroke_primary_light`}
            >
              <div className="flex items-center p-3 border-e border-stroke_primary_light gap-2">
                <IconComponent name="modulesIcon" width="1rem" height="1rem" />
                <Text className="font-semibold">{t("Module")}</Text>
              </div>
              <div className="flex items-center p-3 border-e border-stroke_primary_light gap-2">
                <div className="flex items-center gap-2">
                  <IconComponent name="toggleIcon" width="1rem" height="1rem" />
                  <Text className="font-semibold">{t("Creation Permission")}</Text>
                </div>
                <IconComponent
                  toolTipContent={t(
                    "This represents the ability for the selected user(s) to create any new entities for the respective module.",
                  )}
                  name="details"
                  width="1rem"
                  height="1rem"
                />
              </div>
              <div className="flex items-center p-3 border-e border-stroke_primary_light gap-2">
                <div className="flex items-center gap-2">
                  <IconComponent name="access" width="1rem" height="1rem" />
                  <Text className="font-semibold">{t("Default Access")}</Text>
                </div>
                <IconComponent
                  toolTipContent={t(
                    "This represents the access level which the selected user(s) will have for any entities in the respective module by default.",
                  )}
                  name="details"
                  width="1rem"
                  height="1rem"
                />
              </div>
            </div>
            <div className="flex w-full h-full flex-col">
              {([...moduleMapper, addOnObj] as PermissionGroup[]).map((head) => (
                <div key={head.id} className="border border-stroke_primary_light">
                  <Accordion className="w-full shadow-none border-none m-0" defaultExpanded>
                    <AccordionSummary
                      className="bg-light_background border border-stroke_primary_light"
                      expandIcon={<ExpandMoreIcon className="pe-2" />}
                    >
                      <div className="font-semibold">{head.name}</div>
                    </AccordionSummary>
                    <AccordionDetails className="shadow-none p-0">{permissionContent(head)}</AccordionDetails>
                  </Accordion>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    };
    const handleCloseRecursiveModal = () => {
      setRecursiveChangePermission(false);
      setConfirmationModelOpen({
        status: false,
        module: "",
        value: {},
        permissionName: "",
      });
    };
    const toggleIsRecursive = (event: React.ChangeEvent<HTMLInputElement>) => {
      setRecursiveChangePermission(event.target.value === "overRide");
    };

    const recursiveConfirmationContent = () => {
      let content: string,
        lable1: string,
        label2: string,
        warningMessage: string = "",
        showRecursiveOptions: boolean;
      if (confirmationModelOpen.permissionName == "accessPermission") {
        showRecursiveOptions = showAccessPermissionConfirmationPopup;
        if (confirmationModelOpen.module === defaultModulesMapper.FOLDERS) {
          warningMessage = folderAccessChangeWarning;
        }
        content = `Do you want to update default access of the ${defaultModuleNamesMapper[confirmationModelOpen.module!]} created in this Space?`;
        lable1 = t("Retain the current access level for all previously created entities");
        label2 = t(
          "Override and update access level for all previously created entities and apply this access level to any new entities created in this Space",
        );
      } else if (confirmationModelOpen.permissionName == "changeAccessTemplate") {
        showRecursiveOptions = true;
        content = t("Do you want to update the Access Template for this Space?");
        lable1 = t("Retain the current permissions for all previously created entities");
        label2 = t(
          "Override and update access template for all previously created entities and apply this access template to any new entities created in this Space",
        );
      } else {
        showRecursiveOptions = showCreatePermissionConfirmationPopup;
        lable1 = t("Retain the current access level for all previously created entities");
        content = `Do you want to update creation permission of the ${defaultModuleNamesMapper[confirmationModelOpen.module!]} created in this Space?`;
        label2 = t(
          "Override and update access level for all previously created entities and apply this access level to any new entities created in this Space",
        );
        if (confirmationModelOpen.module === defaultModulesMapper.FOLDERS) {
          warningMessage = t(
            "Updating the Creation Permission for Folders will also update the same for Files, Estimates, Schedules and Contracts",
          );
        } else if (
          [
            defaultModulesMapper.ESTIMATES,
            defaultModulesMapper.SCHEDULES,
            defaultModulesMapper.FILES,
            defaultModulesMapper.CONTRACTS,
          ].includes(confirmationModelOpen.module!)
        ) {
          warningMessage = t("Updating the Creation Permission for {{module}} will also update the same for Folders", {
            module: defaultModuleNamesMapper[confirmationModelOpen.module!],
          });
        }
      }
      if (source === defaultAccessFlowSources.PORTAL_SETTINGS) {
        lable1 = t("Retain the current permissions for all users currently assigned to this template");
        label2 = t(
          "Override and update permissions for all users currently assigned to this template, and apply the updated template to any new users going forward",
        );
        content = `Do you want to update Default Access of the ${defaultModuleNamesMapper[confirmationModelOpen.module!]} in this template assigned to users across your Portal?`;
      }
      return (
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <span>{content}</span>
            {!!warningMessage && <div>{getUpdateModalDisclaimer(warningMessage)}</div>}
          </div>
          {showRecursiveOptions && (
            <div className="flex flex-col gap-4">
              <RadioGroup onChange={toggleIsRecursive}>
                <FormControlLabel
                  checked={!recursiveChangePermission}
                  value="retain"
                  control={<RadioButton />}
                  label={lable1}
                  sx={{ mb: "1rem" }}
                />
                <FormControlLabel
                  value="overRide"
                  checked={recursiveChangePermission}
                  control={<RadioButton />}
                  label={label2}
                />
              </RadioGroup>
            </div>
          )}
        </div>
      );
    };

    const handleChangeAccessPermission = (moduleName: string, roleId: number) => {
      setModulePermissionChangeStatus((prev) => {
        let obj: Record<string, PermissionChangeFlags> = {
          [moduleName]: {
            retain: !recursiveChangePermission && showAccessPermissionConfirmationPopup,
            overRide: recursiveChangePermission && showAccessPermissionConfirmationPopup,
            change: !showAccessPermissionConfirmationPopup,
          },
        };
        if (moduleName === defaultModulesMapper.FOLDERS) {
          obj = {
            ...obj,
            [defaultModulesMapper.ESTIMATES]: {
              retain: !recursiveChangePermission && showAccessPermissionConfirmationPopup,
              overRide: recursiveChangePermission && showAccessPermissionConfirmationPopup,
              change: !showAccessPermissionConfirmationPopup,
            },
            [defaultModulesMapper.SCHEDULES]: {
              retain: !recursiveChangePermission && showAccessPermissionConfirmationPopup,
              overRide: recursiveChangePermission && showAccessPermissionConfirmationPopup,
              change: !showAccessPermissionConfirmationPopup,
            },
            [defaultModulesMapper.FILES]: {
              retain: !recursiveChangePermission && showAccessPermissionConfirmationPopup,
              overRide: recursiveChangePermission && showAccessPermissionConfirmationPopup,
              change: !showAccessPermissionConfirmationPopup,
            },
            [defaultModulesMapper.CONTRACTS]: {
              retain: !recursiveChangePermission && showAccessPermissionConfirmationPopup,
              overRide: recursiveChangePermission && showAccessPermissionConfirmationPopup,
              change: !showAccessPermissionConfirmationPopup,
            },
          };
        }
        return {
          ...prev,
          accessPermission: {
            ...prev.accessPermission,
            ...obj,
          },
        };
      });
      setInitialPermissionMapper((prev) => {
        let obj: ModulePermissionMap = {
          [moduleName]: {
            ...prev[moduleName],
            role: roleId,
            isRoleVaries: false,
            permissions: permissionIdMapper[roleId]!.permissionIds,
          },
        };
        if (moduleName === defaultModulesMapper.FOLDERS) {
          obj = {
            ...obj,
            [defaultModulesMapper.FILES]: {
              ...prev[defaultModulesMapper.FILES],
              role: roleId,
              isRoleVaries: false,
              permissions: permissionIdMapper[roleId]!.permissionIds,
            },
            [defaultModulesMapper.SCHEDULES]: {
              ...prev[defaultModulesMapper.SCHEDULES],
              role: roleId,
              isRoleVaries: false,
              permissions: permissionIdMapper[roleId]!.permissionIds,
            },
            [defaultModulesMapper.ESTIMATES]: {
              ...prev[defaultModulesMapper.ESTIMATES],
              role: roleId,
              isRoleVaries: false,
              permissions: permissionIdMapper[roleId]!.permissionIds,
            },
            [defaultModulesMapper.CONTRACTS]: {
              ...prev[defaultModulesMapper.CONTRACTS],
              role: roleId,
              isRoleVaries: false,
              permissions: permissionIdMapper[roleId]!.permissionIds,
            },
          };
        }
        return {
          ...prev,
          ...obj,
        };
      });

      setChangedPermissionMapper((prev) => {
        const currentObj = prev[moduleName] || initialPermissionMapper[moduleName];
        const contractObj =
          prev[defaultModulesMapper.CONTRACTS] || initialPermissionMapper[defaultModulesMapper.CONTRACTS];
        const filesObj = prev[defaultModulesMapper.FILES] || initialPermissionMapper[defaultModulesMapper.FILES];
        const schedulesObj =
          prev[defaultModulesMapper.SCHEDULES] || initialPermissionMapper[defaultModulesMapper.SCHEDULES];
        const estimatesObj =
          prev[defaultModulesMapper.ESTIMATES] || initialPermissionMapper[defaultModulesMapper.ESTIMATES];
        let obj: ModulePermissionMap = {
          [moduleName]: {
            ...currentObj,
            role: roleId,
            permissions: permissionIdMapper[roleId]!.permissionIds,
            isAccessPermissionRecursive: recursiveChangePermission,
          },
        };
        if (moduleName === defaultModulesMapper.FOLDERS) {
          obj = {
            ...obj,
            [defaultModulesMapper.CONTRACTS]: {
              ...contractObj,
              role: roleId,
              permissions: permissionIdMapper[roleId]!.permissionIds,
              isAccessPermissionRecursive: recursiveChangePermission,
            },
            [defaultModulesMapper.FILES]: {
              ...filesObj,
              role: roleId,
              permissions: permissionIdMapper[roleId]!.permissionIds,
              isAccessPermissionRecursive: recursiveChangePermission,
            },
            [defaultModulesMapper.SCHEDULES]: {
              ...schedulesObj,
              role: roleId,
              permissions: permissionIdMapper[roleId]!.permissionIds,
              isAccessPermissionRecursive: recursiveChangePermission,
            },
            [defaultModulesMapper.ESTIMATES]: {
              ...estimatesObj,
              role: roleId,
              permissions: permissionIdMapper[roleId]!.permissionIds,
              isAccessPermissionRecursive: recursiveChangePermission,
            },
          };
        }
        return {
          ...prev,
          ...obj,
        };
      });
      setAccessTemplateChooserState((prev) => ({
        ...prev,
        show: false,
        state: "Custom",
      }));
      setRecursiveChangePermission(false);
      handleCloseRecursiveModal();
    };

    const handleChangeAccessTemplate = (templateId: string) => {
      const selectedTemplate = accessTemplateDatasource.find((item) => item._id === templateId)?.modulePermissions;
      if (!selectedTemplate) return;
      if (getIsRecursive) {
        getIsRecursive(recursiveChangePermission);
      }
      const accessPermissionStatus: Record<string, PermissionChangeFlags> = {};
      const createPermissionStatus: Record<string, PermissionChangeFlags> = {};
      Object.keys(selectedTemplate).forEach((key: string) => {
        ((accessPermissionStatus[key] = {
          retain: !recursiveChangePermission,
          overRide: recursiveChangePermission,
        }),
          (createPermissionStatus[key] = {
            retain: !recursiveChangePermission,
            overRide: recursiveChangePermission,
          }));
      });

      setModulePermissionChangeStatus((prev) => ({
        ...prev,
        accessPermission: accessPermissionStatus,
        createPermission: createPermissionStatus,
      }));
      setInitialPermissionMapper(() => selectedTemplate);

      setChangedPermissionMapper(() => selectedTemplate);
      setAccessTemplateChooserState((prev) => ({
        ...prev,
        newVal: templateId,
        state: "",
      }));
      setRecursiveChangePermission(false);
      handleCloseRecursiveModal();
    };

    const handleChangePermission = () => {
      // `value` carries a different payload per branch (see the state declaration above), so each
      // branch checks the shape it needs rather than asserting the union down to it.
      const { permissionName, module, value } = confirmationModelOpen;
      if (permissionName === "createPermission") {
        if (module === undefined || typeof value !== "boolean") return;
        changeCreatePermission(module, value);
      } else if (permissionName === "changeAccessTemplate") {
        if (typeof value !== "string") return;
        handleChangeAccessTemplate(value);
      } else {
        const roleId = typeof value === "object" ? value.id : undefined;
        if (module === undefined || roleId === undefined) return;
        handleChangeAccessPermission(module, roleId);
      }
    };

    const getConfirmationPopUpHeaderTitle = () => {
      if (confirmationModelOpen.permissionName === "accessPermission") {
        return t("Change Default Access");
      } else if (confirmationModelOpen.permissionName == "changeAccessTemplate") {
        return t("Change Access Template");
      }
      return t("Change Creation Permission");
    };

    return (
      <div className="flex h-full w-full overflow-auto">
        {confirmationModelOpen.status && (
          <ModalStateDialogComponent
            allowModalPadding={false}
            width="40%"
            isVisible
            onCancel={handleCloseRecursiveModal}
            headerTitle={getConfirmationPopUpHeaderTitle()}
            modelContent={recursiveConfirmationContent()}
            footerContent={
              <div className="flex gap-4">
                <ButtonComponent
                  buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
                  onClick={handleChangePermission}
                  data-testid="nextAddUserBtn"
                >
                  <Text>{t("Save")}</Text>
                </ButtonComponent>
                <ButtonComponent
                  buttonType={BUTTON_TYPES.BORDER_BUTTON}
                  onClick={handleCloseRecursiveModal}
                  data-testid="cancelAddUserBtn"
                >
                  <Text>{t("Cancel")}</Text>
                </ButtonComponent>
              </div>
            }
          />
        )}
        {updatePermissionModalContent()}
      </div>
    );
  },
);
SpaceUsersPermissionFlow.displayName = "SpaceUsersPermissionFlow";
export default SpaceUsersPermissionFlow;
